import React, { useState } from 'react';
import { Select, MenuItem, FormControl, InputLabel, FormHelperText, InputAdornment, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { UseFormRegister, FieldValues, Path } from 'react-hook-form';
import { SxProps } from '@mui/system';
import { useTranslation } from 'react-i18next';

interface FormSelectProps<TFieldValues extends FieldValues> {
    name: Path<TFieldValues>;
    label: string;
    options: { value: string | number; label?: string; title?: string }[];
    register: UseFormRegister<TFieldValues>;
    error?: boolean;
    helperText?: any;
    isDisabled?: boolean;
    size?: 'large' | 'small';
    testId?: string;
    defaultValue?: string | number;
    menuItemsSX?: SxProps;
}

const FormSelect = <TFieldValues extends FieldValues>({
    name,
    label,
    options,
    register,
    error = false,
    helperText = '',
    isDisabled = false,
    size = 'small',
    testId,
    defaultValue = '',
    menuItemsSX = {},
}: FormSelectProps<TFieldValues>) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const handleToggleOpen = () => {
        setOpen(!open);
    };

    return (
        <FormControl
            variant='outlined'
            fullWidth
            error={error}
            disabled={isDisabled}
            size={size === 'small' ? 'small' : 'medium'}
            sx={{
                '& .MuiOutlinedInput-root': {
                    fontSize: '14px',
                    height: '48px',
                    '& fieldset': {
                        borderColor: 'common.grey2',
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderColor: isDisabled ? 'common.grey2' : 'common.black',
                    },
                    '&.Mui-focused fieldset': {
                        borderWidth: '1px',
                        borderColor: 'common.primary',
                    },
                    '&.Mui-error fieldset': {
                        borderColor: 'common.system.error.main',
                    },
                },
                '& .MuiFormHelperText-root.Mui-error': {
                    color: 'common.system.error.main',
                },
            }}
        >
            <InputLabel
                style={{
                    color: 'common.grey1',
                    fontSize: size === 'small' ? '12px' : '14px',
                }}
                sx={{
                    '&.Mui-focused': {
                        color: error ? 'common.system.error.main' : 'common.grey1',
                    },
                }}
            >
                {label}
            </InputLabel>
            <Select
                {...register(name)}
                label={label}
                defaultValue={defaultValue}
                data-testid={testId ? testId : 'form-select'}
                open={open}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 200,
                            zIndex: 9999, // Ensure this zIndex is higher than the dialog's zIndex
                        },
                    },
                }}
                onClose={() => setOpen(false)}
                onOpen={() => setOpen(true)}
                endAdornment={
                    <InputAdornment position='end'>
                        <IconButton
                            onClick={handleToggleOpen}
                            edge='end'
                            disabled={isDisabled}
                            data-testid="iconButton"
                            sx={{ color: error ? 'common.system.error.main' : 'common.grey1' }}
                        >
                            {open ? <ExpandLess data-testid="expandLess" /> : <ExpandMore data-testid="expandMore" />}
                        </IconButton>
                    </InputAdornment>
                }
                IconComponent={() => null}
            >
                <MenuItem value='' sx={{ display: 'none' }}>
                    <em>{t('select-an-option')}</em>
                </MenuItem>
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                            fontSize: '14px',
                            '&:hover': {
                                backgroundColor: 'common.primaryLightSelected',
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'common.primaryLightSelected',
                                '&:hover': {
                                    backgroundColor: 'common.primaryLightSelected',
                                },
                            },
                            ...menuItemsSX,
                        }}
                    >
                        {option.label || option.title}
                    </MenuItem>
                ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

export default FormSelect;
